<template>
  <fieldset :class="['base-fieldset', { flush: flush }]">
    <slot />
  </fieldset>
</template>

<script setup lang="ts">
/**
 * Form fieldset wrapper for grouping related form controls
 *
 * Provides semantic grouping with reset browser styles, flex layout,
 * and consistent spacing. Use with BaseLabel as legend for accessibility.
 *
 * @example Basic fieldset with legend
 * ```vue
 * <BaseFieldset>
 *   <BaseLabel>Contact Information</BaseLabel>
 *   <FormControl v-model="email" type="email" label="Email" />
 *   <FormControl v-model="phone" type="tel" label="Phone" />
 * </BaseFieldset>
 * ```
 *
 * @example Fieldset without legend
 * ```vue
 * <BaseFieldset>
 *   <BaseCheckbox v-model="agree" label="I agree to terms" />
 *   <BaseCheckbox v-model="newsletter" label="Subscribe to newsletter" />
 * </BaseFieldset>
 * ```
 *
 * @example Flush fieldset (no bottom margin)
 * ```vue
 * <BaseFieldset flush>
 *   <BaseRadioGroup
 *     v-model="option"
 *     name="options"
 *     :options="radioOptions"
 *   />
 * </BaseFieldset>
 * ```
 */
export interface BaseFieldsetProps {
  /**
   * Removes bottom margin (useful when fieldset is last child)
   * @default false
   */
  flush?: boolean;
}

withDefaults(defineProps<BaseFieldsetProps>(), {
  flush: false,
});
</script>

<style>
.base-fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--gap-xs);
  border: none;
  padding: 0;
  margin: 0 0 var(--gap-sm) 0;

  &.flush,
  &:last-child {
    margin-bottom: 0;
  }
}
</style>
